/**
 * `SkillWeightEditor` — selezione delle skill di una competenza con il relativo
 * **peso** (importanza) `1..5`.
 *
 * Combina il {@link ChipAutocomplete} (per aggiungere/rimuovere skill) con una
 * tabellina che, per ogni skill selezionata, espone un select `1..5` per il
 * peso. Il peso concorre al calcolo del match della competenza (dashboard e
 * pannello "Livelli"): una skill con peso più alto pesa di più.
 *
 * Il componente è controllato: lo stato (`selectedIds` + `weights`) vive nel
 * genitore. Notifica le due variazioni separatamente:
 *  - `onChangeIds(next)` quando cambia l'insieme delle skill;
 *  - `onChangeWeights(next)` quando cambia un peso.
 *
 * Le skill senza un peso esplicito valgono {@link DEFAULT_SKILL_WEIGHT} (1),
 * coerentemente con il calcolo lato server.
 */
import { useMemo } from 'react';
import {
  DEFAULT_SKILL_WEIGHT,
  MAX_SKILL_WEIGHT,
  MIN_SKILL_WEIGHT,
  type Livello,
} from '../../shared/dto';
import { LivelloGuide } from '../LivelloGuide';
import { LivelloSelect } from '../LivelloSelect';
import { ChipAutocomplete, type ChipOption } from './ChipAutocomplete';
import { DotRating } from './DotRating';
import { InfoHint } from './InfoHint';

interface SkillWeightEditorProps {
  /** `id` del controllo di ricerca (collegato alla `<label>` del `Field`). */
  id: string;
  /** Elenco completo delle skill disponibili. */
  options: readonly ChipOption[];
  /**
   * Descrizione per skill id (facoltativa): se presente, mostra un'icona "i"
   * con l'hint accanto al nome della skill nella tabella dei pesi.
   */
  descriptions?: Record<string, string | undefined>;
  /** `id` delle skill attualmente selezionate. */
  selectedIds: readonly string[];
  /** Pesi correnti per skill id (le skill assenti valgono il default). */
  weights: Record<string, number>;
  /**
   * Livello richiesto corrente per skill id. Le skill assenti dalla mappa non
   * hanno un Livello_Richiesto (nessun default forzato).
   */
  requiredLevels: Record<string, Livello>;
  /** Notifica il nuovo insieme di skill selezionate. */
  onChangeIds: (next: string[]) => void;
  /** Notifica la nuova mappa completa dei pesi. */
  onChangeWeights: (next: Record<string, number>) => void;
  /** Notifica la nuova mappa completa dei Livelli_Richiesti. */
  onChangeRequiredLevels: (next: Record<string, Livello>) => void;
  placeholder?: string;
  emptyMessage?: string;
}

/** Etichetta descrittiva del peso, per orientare l'admin. */
const WEIGHT_HINT: Record<number, string> = {
  1: 'Marginale',
  2: 'Bassa',
  3: 'Media',
  4: 'Alta',
  5: 'Essenziale',
};

export function SkillWeightEditor({
  id,
  options,
  descriptions,
  selectedIds,
  weights,
  requiredLevels,
  onChangeIds,
  onChangeWeights,
  onChangeRequiredLevels,
  placeholder = 'Cerca una skill…',
  emptyMessage = 'Nessuna skill corrisponde.',
}: SkillWeightEditorProps) {
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of options) map.set(o.id, o.name);
    return map;
  }, [options]);

  // Alla rimozione di una skill, ne scartiamo anche l'eventuale peso e Livello
  // richiesto, così le mappe non trattengono voci orfane.
  const handleIdsChange = (next: string[]) => {
    const nextSet = new Set(next);
    const prunedWeights: Record<string, number> = {};
    for (const [skillId, w] of Object.entries(weights)) {
      if (nextSet.has(skillId)) prunedWeights[skillId] = w;
    }
    const prunedLevels: Record<string, Livello> = {};
    for (const [skillId, level] of Object.entries(requiredLevels)) {
      if (nextSet.has(skillId)) prunedLevels[skillId] = level;
    }
    onChangeIds(next);
    onChangeWeights(prunedWeights);
    onChangeRequiredLevels(prunedLevels);
  };

  const setWeight = (skillId: string, weight: number) => {
    onChangeWeights({ ...weights, [skillId]: weight });
  };

  const setRequiredLevel = (skillId: string, level: Livello) => {
    onChangeRequiredLevels({ ...requiredLevels, [skillId]: level });
  };

  // "Qualsiasi livello": rimuove la voce dalla mappa, così la skill non ha un
  // Livello_Richiesto (equivale a non impostare il campo lato server).
  const clearRequiredLevel = (skillId: string) => {
    const next = { ...requiredLevels };
    delete next[skillId];
    onChangeRequiredLevels(next);
  };

  return (
    <div className="skill-weight-editor">
      <ChipAutocomplete
        id={id}
        options={options}
        selectedIds={selectedIds}
        onChange={handleIdsChange}
        placeholder={placeholder}
        emptyMessage={emptyMessage}
      />

      {selectedIds.length > 0 && (
        <table className="skill-weight-editor__table">
          <caption className="sr-only">
            Peso di ciascuna skill sulla competenza (da {MIN_SKILL_WEIGHT} a{' '}
            {MAX_SKILL_WEIGHT})
          </caption>
          <thead>
            <tr>
              <th scope="col">Skill</th>
              <th scope="col" className="skill-weight-editor__level-col">
                <span className="skill-weight-editor__level-head">
                  Livello
                  {/* Icona "i" che apre la guida ai livelli, come nel picker
                      della tabella skill e nel wizard di assessment. */}
                  <LivelloGuide />
                </span>
              </th>
              <th scope="col" className="skill-weight-editor__weight-col">
                Peso
              </th>
            </tr>
          </thead>
          <tbody>
            {selectedIds.map((skillId) => {
              const weight = weights[skillId] ?? DEFAULT_SKILL_WEIGHT;
              const requiredLevel = requiredLevels[skillId] ?? null;
              const skillLabel = nameById.get(skillId) ?? skillId;
              return (
                <tr key={skillId}>
                  <td>
                    <span className="skill-weight-editor__skill-label">
                      {skillLabel}
                      <InfoHint
                        text={descriptions?.[skillId]}
                        label={`Descrizione di ${skillLabel}`}
                      />
                    </span>
                  </td>
                  <td className="skill-weight-editor__level-col">
                    <span
                      className="user-admin__select skill-weight-editor__level-select"
                      data-empty={requiredLevel === null || undefined}
                    >
                      <LivelloSelect
                        value={requiredLevel}
                        onChange={(next) => setRequiredLevel(skillId, next)}
                        onClear={() => clearRequiredLevel(skillId)}
                        label={`Livello di ${skillLabel}`}
                        placeholder="Qualsiasi livello"
                      />
                    </span>
                  </td>
                  <td className="skill-weight-editor__weight-col">
                    <div className="skill-weight-editor__weight">
                      <DotRating
                        value={weight}
                        onChange={(next) => setWeight(skillId, next)}
                        max={MAX_SKILL_WEIGHT}
                        label={`Peso di ${skillLabel}`}
                        optionLabel={(n) =>
                          `${n} — ${WEIGHT_HINT[n] ?? ''}`.trim()
                        }
                      />
                      <span className="skill-weight-editor__weight-hint">
                        {WEIGHT_HINT[weight]}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default SkillWeightEditor;
